import React, { useRef, useState } from "react";

// Same labels, colours and order as the pie the Python report generator drew.
// Each report field is "count,percent,..." (or already split into an array),
// so the slice value is the second item.
const PIE_SLICES = [
  { label: "SR", field: "normal_sr", color: "#008900" },
  { label: "PVC", field: "pvc", color: "#ff19ff" },
  { label: "PAC", field: "pac", color: "#0000ff" },
  { label: "VT", field: "vt", color: "#a64d79" },
  { label: "ST", field: "sinus_tc", color: "#00ffff" },
  { label: "SP", field: "sinus_pause", color: "#FF5B05" },
  { label: "SB", field: "sinus_bc", color: "#ff9900" },
  { label: "AF", field: "afib", color: "#b219ff" },
  { label: "3AVB", field: "at", color: "#98FB98" },
  { label: "CHB", field: "irrr", color: "#A4C3FF" },
  { label: "2AVB", field: "irpr", color: "#BBEB8F" },
  { label: "SVT", field: "svt", color: "#98FB98" },
  { label: "J", field: "j", color: "#A96BFF" },
  { label: "PBT", field: "pbt", color: "#c96936" },
];

const partsOf = (raw) => (Array.isArray(raw) ? raw : String(raw || "").split(","));
const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Beat count and percentage the report holds for a slice ("count,percent,...").
const countOf = (report, field) => num(partsOf(report[field])[0]);
const storedPercent = (report, field) => num(partsOf(report[field])[1]);

// Percentage for a slice: the one stored in the report, or, if that is missing,
// worked out from the beat counts the same way the Python generator did
// (this slice's count / the sum of all slice counts).
// Percentages are always shown and calculated to 2 decimals (like the generator).
const roundPct = (v) => Math.round(v * 100) / 100;
export const fmtPct = (v) => v.toFixed(2);

const percentFor = (report, field) => {
  const stored = storedPercent(report, field);
  if (stored > 0) return stored;
  const total = totalBeatsOf(report);
  if (total <= 0) return 0;
  return roundPct((countOf(report, field) / total) * 100);
};

// Total classified beats behind the report's percentages. The slice with the
// biggest percentage gives it back exactly (count / percent * 100); fall back to
// the sum of all counts, then to the analysed-beats total.
export const totalBeatsOf = (report = {}) => {
  let best = null;
  PIE_SLICES.forEach(({ field }) => {
    if (field === "sinus_pause") return; // its first item is not a beat count
    const count = countOf(report, field);
    const pct = storedPercent(report, field);
    if (count > 0 && pct > 0 && (!best || pct > best.pct)) best = { count, pct };
  });
  if (best) return (best.count / best.pct) * 100;
  const sum = PIE_SLICES.reduce((t, s) => t + countOf(report, s.field), 0);
  return sum > 0 ? sum : num(report.total_beatscount);
};

const CUSTOM_COLORS = ["#e6194b", "#3cb44b", "#ffe119", "#4363d8", "#f58231", "#911eb4", "#46f0f0", "#f032e6"];

// Like the generated chart, a slice is shown whenever the report has beats for
// it, even if its percentage rounds to 0.00.
//
// `added` = arrhythmias the verifier added by hand: [{ label, count }]. Each
// slice's percentage is then its beat count divided by the total beats
// (report's beats + the added ones), times 100, so the pie still adds up to 100%.
export const buildPieSlices = (report = {}, added = []) => {
  const present = PIE_SLICES.filter(
    ({ field }) => countOf(report, field) > 0 || storedPercent(report, field) > 0
  );
  if (!added.length) {
    return present.map(({ label, field, color }) => ({ label, color, value: percentFor(report, field) }));
  }

  const T = totalBeatsOf(report);
  const items = present.map(({ label, field, color }) => {
    const stored = storedPercent(report, field);
    return { label, color, count: stored > 0 ? (stored * T) / 100 : countOf(report, field) };
  });
  let customIdx = 0;
  added.forEach(({ label, count }) => {
    const c = num(count);
    if (!label || c <= 0) return;
    const hit = items.find((i) => i.label.toLowerCase() === String(label).toLowerCase());
    if (hit) {
      hit.count = c; // the beats typed by the reporter replace the report's count
      return;
    }
    const known = PIE_SLICES.find((p) => p.label.toLowerCase() === String(label).toLowerCase());
    items.push({
      label: known ? known.label : String(label),
      color: known ? known.color : CUSTOM_COLORS[customIdx++ % CUSTOM_COLORS.length],
      count: c,
    });
  });
  // keep the report's usual order, custom labels last
  const order = (l) => {
    const i = PIE_SLICES.findIndex((p) => p.label === l);
    return i === -1 ? PIE_SLICES.length : i;
  };
  items.sort((x, y) => order(x.label) - order(y.label));
  const total = items.reduce((t, i) => t + i.count, 0);
  if (total <= 0) return [];
  return items.map(({ label, color, count }) => ({
    label,
    color,
    // same as the generator: round((count / total_count) * 100, 2)
    value: Math.round((count / total) * 10000) / 100,
  }));
};

const SIZE = 80;
const R = SIZE / 2;

const slicePath = (start, end) => {
  if (end - start >= Math.PI * 2 - 1e-6) {
    return `M ${R} 0 A ${R} ${R} 0 1 1 ${R} ${SIZE} A ${R} ${R} 0 1 1 ${R} 0 Z`;
  }
  const x1 = R + R * Math.cos(start);
  const y1 = R + R * Math.sin(start);
  const x2 = R + R * Math.cos(end);
  const y2 = R + R * Math.sin(end);
  const large = end - start > Math.PI ? 1 : 0;
  return `M ${R} ${R} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;
};

// The pie as an SVG image (data URI), used for the PDF capture.
const pieImageSrc = (slices, total) => {
  let a = -Math.PI / 2;
  const paths = slices
    .map((s) => {
      const sweep = (s.value / total) * Math.PI * 2;
      const d = slicePath(a, a + sweep);
      a += sweep;
      return `<path d="${d}" fill="${s.color}"/>`;
    })
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${SIZE}" height="${SIZE}" viewBox="0 0 ${SIZE} ${SIZE}">${paths}</svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
};

// Removes the given slices in order; each removed slice's percentage is added
// to whichever remaining slice has the highest percentage at that moment.
export const applyRemovals = (base, removedLabels) => {
  let list = base.map((s) => ({ ...s }));
  removedLabels.forEach((label) => {
    if (list.length <= 1) return;
    const idx = list.findIndex((s) => s.label === label);
    if (idx === -1) return;
    const [gone] = list.splice(idx, 1);
    let top = 0;
    list.forEach((s, i) => {
      if (s.value > list[top].value) top = i;
    });
    list[top] = { ...list[top], value: list[top].value + gone.value };
  });
  return list;
};

// Pie chart drawn in the browser from the report data (replaces the PNG the
// Python script used to generate).
//  - Click a legend row or a slice to select it, then press Delete to remove
//    that arrhythmia; its percentage moves to the largest remaining slice.
//  - Click the chart background and press Delete to remove the whole chart.
const PieChart = ({
  report,
  className,
  removed,
  onRemovedChange,
  added,
  onAddedChange,
  hideControls = false,
}) => {
  // Removals are owned by the parent (so they can be saved with the report);
  // fall back to local state if no parent is wired up.
  const [localRemoved, setLocalRemoved] = useState([]);
  const removedAll = removed || localRemoved;
  const setRemovedAll = (next) => (onRemovedChange || setLocalRemoved)(next);
  const chartRemoved = removedAll.includes("__chart");
  const removedLabels = removedAll.filter((l) => l !== "__chart");
  const setRemovedLabels = (fn) => {
    const nextLabels = typeof fn === "function" ? fn(removedLabels) : fn;
    setRemovedAll([...nextLabels, ...(chartRemoved ? ["__chart"] : [])]);
  };
  // Arrhythmias added by the verifier: [{ label, count }], also owned by the parent.
  const [localAdded, setLocalAdded] = useState([]);
  const addedList = added || localAdded;
  const setAddedList = (next) => (onAddedChange || setLocalAdded)(next);
  const labelRef = useRef(null);
  const countRef = useRef(null);
  const [formError, setFormError] = useState("");
  const [selected, setSelected] = useState(null); // slice label | "chart" | null
  const [adding, setAdding] = useState(false);
  const [chosenLabel, setChosenLabel] = useState("");
  const [overrides, setOverrides] = useState({}); // label -> percentage typed in the Put back panel
  const [beatsTyped, setBeatsTyped] = useState({}); // label -> beats typed in the Put back panel

  if (chartRemoved) return null;

  const base = buildPieSlices(report, addedList).map((b) =>
    overrides[b.label] !== undefined
      ? { ...b, value: roundPct(Math.max(0, parseFloat(overrides[b.label]) || 0)) }
      : b
  );
  const slices = applyRemovals(base, removedLabels);
  if (!slices.length) return null;

  // Labels that can be added back: arrhythmias the report has data for that are
  // not in the pie now (ones removed earlier), with their % from the report.
  const options = removedLabels
    .filter((label, i) => removedLabels.indexOf(label) === i)
    .filter((label) => !slices.some((x) => x.label === label))
    .map((label) => ({ label, value: (base.find((b) => b.label === label) || {}).value || 0 }));
  const chosen = options.find((o) => o.label === chosenLabel) || options[0];

  const total = slices.reduce((sum, s) => sum + s.value, 0);
  const totalBeats = totalBeatsOf(report);
  let angle = -Math.PI / 2;

  const addLabel = () => {
    if (!chosen) return;
    // Put the removed slice back: the % it gave away is taken back from the slice that got it.
    setRemovedLabels((prev) => prev.filter((l) => l !== chosen.label));
    setChosenLabel("");
    setAdding(false);
  };

  // Add an arrhythmia the analysis missed: the verifier gives its beat count and
  // the percentages are recalculated from the counts.
  const addMissed = () => {
    const label = (labelRef.current ? labelRef.current.value : "").trim();
    const count = parseFloat(countRef.current ? countRef.current.value : "");
    if (!label) return setFormError("Enter the arrhythmia name.");
    if (!Number.isFinite(count) || count <= 0) return setFormError("Enter the number of beats (above 0).");
    if (totalBeatsOf(report) <= 0) return setFormError("This report has no beat total to calculate from.");
    const known = PIE_SLICES.find((p) => p.label.toLowerCase() === label.toLowerCase());
    const finalLabel = known ? known.label : label;
    const exists = addedList.find((a) => a.label.toLowerCase() === finalLabel.toLowerCase());
    setAddedList(
      exists
        ? addedList.map((a) => (a === exists ? { ...a, count } : a))
        : [...addedList, { label: finalLabel, count }]
    );
    // If this slice had been removed earlier, show it again.
    if (removedLabels.includes(finalLabel)) {
      setRemovedLabels((prev) => prev.filter((l) => l !== finalLabel));
    }
    setFormError("");
    setAdding(false);
  };

  const handleKeyDown = (e) => {
    if (e.target.tagName === "SELECT" || e.target.tagName === "INPUT") return;
    if (e.key !== "Delete" && e.key !== "Backspace") return;
    if (!selected) return;
    e.preventDefault();
    if (selected === "chart") {
      setRemovedAll([...removedAll, "__chart"]);
    } else if (slices.length > 1) {
      setRemovedLabels((prev) => [...prev, selected]);
      setSelected(null);
    }
  };

  const pick = (label) => (e) => {
    e.stopPropagation();
    setSelected(label);
  };

  return (
    <div
      className={className}
      tabIndex={0}
      title="Click a slice or legend row and press Delete to remove it"
      onClick={() => setSelected("chart")}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setSelected(null);
      }}
      onKeyDown={handleKeyDown}
      style={{
        height: 103,
        zIndex: 50,
        outline: !hideControls && selected === "chart" ? "1px dashed #1a73e8" : "none",
        cursor: "pointer",
      }}
    >
      {hideControls ? (
        // While the PDF is captured, the pie is given as a plain image: the
        // capture library does not draw inline <svg> elements, so the pie would
        // be missing from the downloaded report.
        <img
          alt=""
          width={SIZE}
          height={SIZE}
          src={pieImageSrc(slices, total)}
          style={{ position: "absolute", left: 118, top: (103 - SIZE) / 2 }}
        />
      ) : (
        <svg
          width={SIZE}
          height={SIZE}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          style={{ position: "absolute", left: 118, top: (103 - SIZE) / 2 }}
        >
          {slices.map((s) => {
            const sweep = (s.value / total) * Math.PI * 2;
            const d = slicePath(angle, angle + sweep);
            angle += sweep;
            return (
              <path
                key={s.label}
                d={d}
                fill={s.color}
                stroke={selected === s.label ? "#1a73e8" : "none"}
                strokeWidth={selected === s.label ? 2 : 0}
                onClick={pick(s.label)}
              />
            );
          })}
        </svg>
      )}
      <div
        style={{
          position: "absolute",
          right: 5,
          top: "50%",
          transform: "translateY(-50%)",
          border: "1px solid #000",
          borderRadius: 2,
          padding: "3px 4px",
          background: "#fff",
          minWidth: 68,
          fontSize: 7,
          lineHeight: "10px",
          fontFamily: "DejaVu Sans, Verdana, sans-serif",
          whiteSpace: "nowrap",
        }}
      >
        {slices.map((s) => (
          <div
            key={s.label}
            onClick={pick(s.label)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              height: 10,
              marginBottom: 1,
              background: !hideControls && selected === s.label ? "#cfe3ff" : "transparent",
            }}
          >
            <span style={{ width: 14, height: 6, background: s.color, flexShrink: 0 }} />
            <span>{`${s.label}, ${fmtPct(s.value)} %`}</span>
          </div>
        ))}
      </div>

      {!hideControls && (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setAdding(true);
        }}
        style={{
          position: "absolute",
          right: 5,
          top: 0,
          fontSize: 8,
          lineHeight: 1,
          padding: "2px 4px",
          cursor: "pointer",
        }}
      >
        + Add label
      </button>
      )}

      {adding && !hideControls && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            width: 230,
            background: "#fff",
            border: "1px solid #444",
            boxShadow: "0 2px 8px rgba(0,0,0,.3)",
            padding: 8,
            zIndex: 100,
            fontFamily: "Arial, sans-serif",
            fontSize: 12,
            cursor: "default",
          }}
        >
          {options.length ? (
            <>
              <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                <select
                  value={chosen ? chosen.label : ""}
                  onChange={(e) => setChosenLabel(e.target.value)}
                  style={{ flex: 1, height: 24, fontSize: 12 }}
                >
                  {options.map((o) => (
                    <option key={o.label} value={o.label}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {chosen && (
                  <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      title="Percentage"
                      value={overrides[chosen.label] !== undefined ? overrides[chosen.label] : chosen.value.toFixed(2)}
                      onChange={(e) => {
                        // keep at most 2 decimal places; beats follow the percentage
                        const v = e.target.value.replace(/^(\d*\.\d{0,2}).*$/, "$1");
                        setOverrides((o) => ({ ...o, [chosen.label]: v }));
                        setBeatsTyped((t) => {
                          const { [chosen.label]: _drop, ...rest } = t;
                          return rest;
                        });
                      }}
                      style={{ width: 54, height: 22, fontSize: 12 }}
                    />
                    %
                    <input
                      type="number"
                      min="0"
                      step="1"
                      placeholder="Beats"
                      title="Beats"
                      value={
                        beatsTyped[chosen.label] !== undefined
                          ? beatsTyped[chosen.label]
                          : totalBeats > 0
                          ? Math.round(((parseFloat(overrides[chosen.label] !== undefined ? overrides[chosen.label] : chosen.value) || 0) / 100) * totalBeats)
                          : ""
                      }
                      onChange={(e) => {
                        const raw = e.target.value;
                        const n = parseFloat(raw);
                        setBeatsTyped((t) => ({ ...t, [chosen.label]: raw }));
                        if (totalBeats > 0) {
                          // same formula as the generator: round((beats / total_count) * 100, 2)
                          const pct = Number.isFinite(n) && n > 0 ? roundPct((n / totalBeats) * 100) : 0;
                          setOverrides((o) => ({ ...o, [chosen.label]: pct.toFixed(2) }));
                        }
                      }}
                      style={{ width: 56, height: 22, fontSize: 12 }}
                    />
                  </span>
                )}
              </div>
            </>
          ) : (
            <div style={{ fontSize: 11, marginBottom: 6 }}>
              Nothing was removed from the chart.
            </div>
          )}
          {options.length > 0 && (
            <button type="button" onClick={addLabel} style={{ width: "100%", height: 24, marginBottom: 8 }}>
              Put back {chosen ? chosen.label : ""}
            </button>
          )}
          <div style={{ borderTop: "1px solid #ccc", paddingTop: 6, marginBottom: 4, fontWeight: "bold", fontSize: 11 }}>
            Add an arrhythmia missed by the analysis
          </div>
          <div style={{ display: "flex", gap: 4, marginBottom: 4 }}>
            <input
              ref={labelRef}
              list="pie-arrhythmia-labels"
              placeholder="Name (e.g. PVC)"
              style={{ flex: 1, minWidth: 0, height: 22, fontSize: 12, boxSizing: "border-box" }}
            />
            <datalist id="pie-arrhythmia-labels">
              {PIE_SLICES.map((p) => (
                <option key={p.label} value={p.label} />
              ))}
            </datalist>
            <input
              ref={countRef}
              placeholder="Beats"
              inputMode="numeric"
              style={{ width: 56, height: 22, fontSize: 12, boxSizing: "border-box" }}
            />
          </div>
          {formError && <div style={{ color: "#c00", fontSize: 11, marginBottom: 4 }}>{formError}</div>}
          <div style={{ display: "flex", gap: 6 }}>
            <button type="button" onClick={addMissed} style={{ flex: 1, height: 24 }}>
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setFormError("");
              }}
              style={{ flex: 1, height: 24 }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PieChart;
